/**
 * AI Review Page - Post-generation review workspace for AI-generated funnels (MA-FE-073)
 * 
 * MA-AI-REVIEW-STEP-DECOUPLE-144: FEATURE DISABLED IN STANDALONE FLOW
 * =====================================================================
 * Status: disabled_standalone
 * Intended Surface: PPB (Profit Path Builder) or advanced onboarding
 * 
 * This feature is intentionally decoupled from Standalone Funnel Builder
 * to reduce friction and simplify the generate → edit workflow.
 * 
 * DO NOT REMOVE without product-level approval.
 * 
 * Re-enablement: Update FunnelsPage.jsx nav route from `/builder` to `/review`
 * =====================================================================
 * 
 * Flow: FunnelAssist generates funnel → AI Review opens → User reviews/edits → Open Builder
 * 
 * Features:
 * - Step-by-step review of AI-generated funnel
 * - Inline editing via Mutation Engine
 * - AI context explanations
 * - Routing preview
 * - Approval tracking (UI state only)
 */

import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useCanonicalFunnelGraph } from "@/hooks/useCanonicalFunnelGraph";
import { useMutate } from "@/hooks/useFunnels";
import { cn } from "@/lib/utils";

// UI Components
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Progress } from "@/components/ui/progress";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

// Icons
import {
  ArrowLeft,
  ArrowRight,
  Check,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Edit3,
  Eye,
  Loader2,
  MessageSquare,
  HelpCircle,
  TextCursorInput,
  Shield,
  Calendar,
  PhoneForwarded,
  GitBranch,
  Circle,
  Sparkles,
  AlertTriangle,
  Info,
  Target,
  TrendingUp,
  AlertCircle,
  ExternalLink,
  RefreshCw,
  SkipForward,
  CheckCheck,
  Home,
  Wrench,
} from "lucide-react";

// Sub-components
import AIReviewStepNavigator from "@/components/funnels/ai-review/AIReviewStepNavigator";
import AIReviewStepCard from "@/components/funnels/ai-review/AIReviewStepCard";
import AIReviewContextPanel from "@/components/funnels/ai-review/AIReviewContextPanel";
import AIReviewCompletionScreen from "@/components/funnels/ai-review/AIReviewCompletionScreen";

import { LOGO_COLLAPSED_DATA } from "@/assets/logoInline";
import { toast } from "@/hooks/use-toast";

/**
 * AI Review Header
 */
function AIReviewHeader({ funnel, nodeCount, onOpenBuilder, onRegenerate }) {
  const navigate = useNavigate();
  
  return (
    <header className="h-14 bg-slate-900/95 border-b border-slate-700/50 flex items-center px-4 gap-4">
      {/* Logo */}
      <Link to="/app/funnels" className="flex items-center gap-2 text-slate-400 hover:text-slate-200 transition-colors">
        <img src={LOGO_COLLAPSED_DATA} alt="Logo" className="h-6 w-6" />
      </Link>
      
      <Separator orientation="vertical" className="h-6 bg-slate-700" />
      
      {/* Title */}
      <div className="flex items-center gap-3 flex-1">
        <Sparkles className="h-5 w-5 text-purple-400" />
        <div>
          <h1 className="text-sm font-medium text-slate-100">AI Review</h1>
          <p className="text-xs text-slate-400">
            {funnel?.name || "Generated Funnel"} • {nodeCount} steps
          </p>
        </div>
        <Badge variant="outline" className="ml-2 text-purple-300 border-purple-500/30 bg-purple-500/10">
          Generated by FunnelAssist
        </Badge>
      </div>
      
      {/* Actions */}
      <div className="flex items-center gap-2">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="text-slate-400 hover:text-slate-200"
                onClick={onRegenerate}
              >
                <RefreshCw className="h-4 w-4 mr-1" />
                Regenerate
              </Button>
            </TooltipTrigger>
            <TooltipContent>Generate a new funnel with different parameters</TooltipContent>
          </Tooltip>
        </TooltipProvider>
        
        <Button
          size="sm"
          onClick={onOpenBuilder}
          className="bg-purple-600 hover:bg-purple-700"
        >
          <Wrench className="h-4 w-4 mr-1" />
          Open Builder
        </Button>
      </div>
    </header>
  );
}

/**
 * AI Review Footer
 */
function AIReviewFooter({
  currentStepIndex,
  totalSteps,
  onPrevious,
  onNext,
  onSkip,
  onApproveStep,
  onApproveAll,
  onOpenBuilder,
  isApproved,
  isFirstStep,
  isLastStep,
}) {
  return (
    <footer className="h-16 bg-slate-900/95 border-t border-slate-700/50 flex items-center justify-between px-6">
      {/* Left: Progress */}
      <div className="flex items-center gap-4">
        <span className="text-sm text-slate-400">
          Step {currentStepIndex + 1} of {totalSteps}
        </span>
        <Progress 
          value={((currentStepIndex + 1) / totalSteps) * 100} 
          className="w-32 h-2 bg-slate-700"
        />
      </div>
      
      {/* Center: Navigation */}
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={onPrevious}
          disabled={isFirstStep}
          className="border-slate-600 text-slate-300"
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Previous
        </Button>
        
        <Button
          variant="ghost"
          size="sm"
          onClick={onSkip}
          disabled={isLastStep}
          className="text-slate-400"
        >
          <SkipForward className="h-4 w-4 mr-1" />
          Skip
        </Button>
        
        <Button
          variant="outline"
          size="sm"
          onClick={onNext}
          disabled={isLastStep}
          className="border-slate-600 text-slate-300"
        >
          Next
          <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </div>
      
      {/* Right: Approve Actions */}
      <div className="flex items-center gap-2">
        <Button
          variant={isApproved ? "outline" : "default"}
          size="sm"
          onClick={onApproveStep}
          className={cn(
            isApproved 
              ? "border-green-600 text-green-400 bg-green-500/10" 
              : "bg-emerald-600 hover:bg-emerald-700"
          )}
        >
          {isApproved ? (
            <>
              <CheckCircle2 className="h-4 w-4 mr-1" />
              Approved
            </>
          ) : (
            <>
              <Check className="h-4 w-4 mr-1" />
              Approve Step
            </>
          )}
        </Button>
        
        <Button
          variant="ghost"
          size="sm"
          onClick={onApproveAll}
          className="text-slate-400 hover:text-slate-200"
        >
          <CheckCheck className="h-4 w-4 mr-1" />
          Approve All
        </Button>
        
        {isLastStep && (
          <Button
            size="sm"
            onClick={onOpenBuilder}
            className="bg-purple-600 hover:bg-purple-700 ml-2"
          >
            Complete Review
            <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        )}
      </div>
    </footer>
  );
}

/**
 * Error State Component
 */
function AIReviewError({ error, onRetry, onGoBack }) {
  return (
    <div className="flex-1 flex items-center justify-center bg-slate-950">
      <Card className="glass border-red-500/30 max-w-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-red-400">
            <AlertCircle className="h-5 w-5" />
            Review Not Available
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-slate-300">
            {error?.message || "Generated funnel structure is invalid. Please regenerate the funnel."}
          </p>
          
          {error?.errors && (
            <ul className="text-sm text-slate-400 list-disc list-inside">
              {error.errors.map((e, i) => (
                <li key={i}>{e}</li>
              ))}
            </ul>
          )}
          
          <div className="flex gap-2 pt-4">
            <Button variant="outline" onClick={onGoBack} className="border-slate-600">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Funnels
            </Button>
            <Button onClick={onRetry} className="bg-purple-600 hover:bg-purple-700">
              <RefreshCw className="h-4 w-4 mr-1" />
              Try Again
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Loading State Component
 */
function AIReviewLoading() {
  return (
    <div className="flex-1 flex items-center justify-center bg-slate-950">
      <div className="text-center space-y-4">
        <Loader2 className="h-8 w-8 animate-spin text-purple-400 mx-auto" />
        <p className="text-slate-400">Loading AI Review...</p>
      </div>
    </div>
  );
}

/**
 * Main AI Review Page Component
 */
export default function AIReviewPage() {
  const { id: funnelId } = useParams();
  const navigate = useNavigate();
  
  // Load canonical funnel graph
  const {
    funnel,
    graph,
    reviewOrder,
    isLoading,
    error,
    isValid,
    isAIGenerated,
    nodeCount,
    draftVersion,
    invalidateAndRefetch,
    getNode,
    getRouting,
    getStepNumber,
  } = useCanonicalFunnelGraph(funnelId);
  
  // Mutation hook for edits
  const { mutateAsync: applyMutation, isPending: isMutating } = useMutate();
  
  // UI State (local only - not persisted)
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [approvedSteps, setApprovedSteps] = useState(new Set());
  const [editedSteps, setEditedSteps] = useState(new Set());
  const [isComplete, setIsComplete] = useState(false);
  
  // Current step
  const currentStep = reviewOrder[currentStepIndex] || null;
  const currentRouting = currentStep ? getRouting(currentStep.id) : [];
  
  // Check if not AI-generated and redirect to builder
  useEffect(() => {
    if (funnel && !funnel.ai_generated) {
      navigate(`/app/funnels/${funnelId}`, { replace: true });
    }
  }, [funnel, funnelId, navigate]);
  
  // Navigation handlers
  const handlePrevious = useCallback(() => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(prev => prev - 1);
    }
  }, [currentStepIndex]);
  
  const handleNext = useCallback(() => {
    if (currentStepIndex < reviewOrder.length - 1) {
      setCurrentStepIndex(prev => prev + 1);
    } else {
      setIsComplete(true);
    }
  }, [currentStepIndex, reviewOrder.length]);
  
  const handleSkip = useCallback(() => {
    handleNext();
  }, [handleNext]);
  
  const handleSelectStep = useCallback((index) => {
    setCurrentStepIndex(index);
    setIsComplete(false);
  }, []);
  
  // Approval handlers
  const handleApproveStep = useCallback(() => {
    if (currentStep) {
      setApprovedSteps(prev => new Set([...prev, currentStep.id]));
      // Auto-advance to next step
      if (currentStepIndex < reviewOrder.length - 1) {
        setTimeout(() => handleNext(), 300);
      }
    }
  }, [currentStep, currentStepIndex, reviewOrder.length, handleNext]);
  
  const handleApproveAll = useCallback(() => {
    const allIds = reviewOrder.map(s => s.id);
    setApprovedSteps(new Set(allIds));
    toast({
      title: "All steps approved",
      description: "Ready to open the Funnel Builder",
    });
  }, [reviewOrder]);
  
  // Edit handler - uses Mutation Engine
  const handleEditStep = useCallback(async (nodeId, field, value) => {
    try {
      // Build mutation based on field
      let mutations = [];
      
      if (field === "label") {
        mutations = [{
          type: "update_node",
          node_id: nodeId,
          changes: { "metadata.label": value },
        }];
      } else if (field === "content.message") {
        mutations = [{
          type: "update_node",
          node_id: nodeId,
          changes: { "content.message": value },
        }];
      } else if (field.startsWith("content.responses")) {
        // Handle response option edits
        mutations = [{
          type: "update_node",
          node_id: nodeId,
          changes: { [field]: value },
        }];
      } else {
        // Generic content update
        mutations = [{
          type: "update_node",
          node_id: nodeId,
          changes: { [field]: value },
        }];
      }
      
      const result = await applyMutation({
        funnelId,
        baseVersion: draftVersion,
        mutations,
        summary: `AI Review: Edit ${field}`,
      });
      
      if (result.status === "applied") {
        setEditedSteps(prev => new Set([...prev, nodeId]));
        // Refetch to get updated graph
        await invalidateAndRefetch();
        toast({
          title: "Step updated",
          description: "Changes saved successfully",
        });
      } else if (result.status === "conflict") {
        toast({
          title: "Version conflict",
          description: "Please refresh and try again",
          variant: "destructive",
        });
      }
    } catch (err) {
      toast({
        title: "Edit failed",
        description: err.message || "Failed to save changes",
        variant: "destructive",
      });
    }
  }, [funnelId, draftVersion, applyMutation, invalidateAndRefetch]);
  
  // Navigation actions
  const handleOpenBuilder = useCallback(() => {
    navigate(`/app/funnels/${funnelId}`);
  }, [funnelId, navigate]);
  
  const handleRegenerate = useCallback(() => {
    navigate("/app/funnels?regenerate=true");
  }, [navigate]);
  
  const handleGoBack = useCallback(() => {
    navigate("/app/funnels");
  }, [navigate]);
  
  const handleRetry = useCallback(() => {
    invalidateAndRefetch();
  }, [invalidateAndRefetch]);
  
  // Completion actions
  const handleCompleteReview = useCallback(() => {
    handleOpenBuilder();
  }, [handleOpenBuilder]);
  
  const handlePreviewFunnel = useCallback(() => {
    // Open preview in new tab
    window.open(`/funnel?slug=${funnel?.slug}&preview=true`, "_blank");
  }, [funnel?.slug]);
  
  const handleReturnToDashboard = useCallback(() => {
    navigate("/app/funnels");
  }, [navigate]);
  
  // Loading state
  if (isLoading) {
    return (
      <div className="h-screen flex flex-col bg-slate-950">
        <AIReviewLoading />
      </div>
    );
  }
  
  // Error state
  if (error || !isValid) {
    return (
      <div className="h-screen flex flex-col bg-slate-950">
        <AIReviewError 
          error={error} 
          onRetry={handleRetry} 
          onGoBack={handleGoBack} 
        />
      </div>
    );
  }
  
  // Completion screen
  if (isComplete) {
    return (
      <div className="h-screen flex flex-col bg-slate-950">
        <AIReviewHeader
          funnel={funnel}
          nodeCount={nodeCount}
          onOpenBuilder={handleOpenBuilder}
          onRegenerate={handleRegenerate}
        />
        <AIReviewCompletionScreen
          stepsReviewed={reviewOrder.length}
          stepsApproved={approvedSteps.size}
          stepsEdited={editedSteps.size}
          onOpenBuilder={handleCompleteReview}
          onPreview={handlePreviewFunnel}
          onReturnToDashboard={handleReturnToDashboard}
        />
      </div>
    );
  }
  
  // Main review interface
  return (
    <div className="h-screen flex flex-col bg-slate-950" data-testid="ai-review-page">
      <AIReviewHeader
        funnel={funnel}
        nodeCount={nodeCount}
        onOpenBuilder={handleOpenBuilder}
        onRegenerate={handleRegenerate}
      />
      
      <div className="flex-1 flex overflow-hidden">
        {/* Left: Step Navigator */}
        <AIReviewStepNavigator
          steps={reviewOrder}
          currentStepIndex={currentStepIndex}
          approvedSteps={approvedSteps}
          editedSteps={editedSteps}
          onSelectStep={handleSelectStep}
        />
        
        {/* Center: Step Review Card */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <AIReviewStepCard
            step={currentStep}
            stepNumber={currentStepIndex + 1}
            totalSteps={reviewOrder.length}
            routing={currentRouting}
            reviewOrder={reviewOrder}
            isApproved={currentStep ? approvedSteps.has(currentStep.id) : false}
            isEdited={currentStep ? editedSteps.has(currentStep.id) : false}
            isSaving={isMutating}
            onEdit={handleEditStep}
          />
        </div>
        
        {/* Right: AI Context Panel */}
        <AIReviewContextPanel
          step={currentStep}
          graph={graph}
          stepNumber={currentStepIndex + 1}
          totalSteps={reviewOrder.length}
        />
      </div>
      
      <AIReviewFooter
        currentStepIndex={currentStepIndex}
        totalSteps={reviewOrder.length}
        onPrevious={handlePrevious}
        onNext={handleNext}
        onSkip={handleSkip}
        onApproveStep={handleApproveStep}
        onApproveAll={handleApproveAll}
        onOpenBuilder={handleOpenBuilder}
        isApproved={currentStep ? approvedSteps.has(currentStep.id) : false}
        isFirstStep={currentStepIndex === 0}
        isLastStep={currentStepIndex === reviewOrder.length - 1}
      />
    </div>
  );
}
